// Copyright (c) 2025 AssetCare.AI Inc. All rights reserved.

// JavaScript for handling authentication with Keycloak.
// See https://www.keycloak.org/securing-apps/javascript-adapter
import Keycloak from 'keycloak-js/lib/keycloak.js';

// We'll create a global object to hold our application's state,
// including the authentication token.
window.appState = {
    keycloak: null,
    isAuthenticated: false,
    idToken: null,
    user: {},
    // You can add more state properties here as needed
};

// Create a promise to handle the asynchronous Keycloak initialization
const authPromise = new Promise((resolve, reject) => {
    // Get the environment-specific Keycloak configuration
    function getKeycloakConfig() {
        // Get the hostname of the current page
        const currentHostname = window.location.hostname;
        
        let keycloakUrl = '';
        let keycloakRealm = '';
        let keycloakClientId = 'home';

        // Determine the environment based on the hostname
        if (currentHostname.includes('dev.assetcare.ai')) {
            keycloakUrl = 'https://authorization.dev.assetcare.ai/auth';
            keycloakRealm = 'dev';
        } else if (currentHostname.includes('test.assetcare.ai')) {
            keycloakUrl = 'https://authorization.tst.assetcare.ai/auth';
            keycloakRealm = 'tst';
        } else if (currentHostname.includes('assetcare.ai')) {
            keycloakUrl = 'https://authorization.prd.assetcare.ai/auth';
            keycloakRealm = 'prd';
        } else {    // Use development for local testing
            keycloakUrl = 'https://authorization.dev.assetcare.ai/auth';
            keycloakRealm = 'dev';
        }

        return {
            url: keycloakUrl,
            realm: keycloakRealm,
            clientId: keycloakClientId
        };
    }

    const config = getKeycloakConfig();
    const keycloak = new Keycloak(config);
    window.appState.keycloak = keycloak;

    keycloak.init({
        onLoad: 'login-required'
    }).then(authenticated => {
        if (authenticated) {
            console.log("User authenticated: " + keycloak.tokenParsed.preferred_username);
            
            // Populate the global app state with user information
            window.appState.isAuthenticated = true;
            window.appState.idToken = keycloak.idToken;
            window.appState.user = {
                preferred_username: keycloak.tokenParsed.preferred_username,
                name: keycloak.tokenParsed.name,
                given_name: keycloak.tokenParsed.given_name,
                family_name: keycloak.tokenParsed.family_name,
                email: keycloak.tokenParsed.email,
                org_id: keycloak.tokenParsed.org_id,
                resource_access: keycloak.tokenParsed.resource_access
            };

            // Call a function from app.js to render the profile if it exists
            if (typeof window.renderUserProfile === 'function') {
                window.renderUserProfile();
            }

            // The profile picture is NOT a token claim — it comes from the userinfo
            // endpoint. A Google photo URL can exceed 1,100 characters, which in both the
            // ID and access token is enough to overflow the cookie/header budget and break
            // login, so the realm delivers `picture` through userinfo only.
            //
            // Fetched after the first render rather than awaited before it: the avatar is
            // decoration, and app.js already falls back to images/user.png while it is
            // absent. Re-render once it arrives so the photo swaps in.
            keycloak.loadUserInfo().then(userInfo => {
                if (userInfo && userInfo.picture) {
                    window.appState.user.picture = userInfo.picture;
                    if (typeof window.renderUserProfile === 'function') {
                        window.renderUserProfile();
                    }
                }
            }).catch(error => {
                // Never fatal — the placeholder avatar stands in.
                console.warn("Could not load userinfo for the profile picture:", error);
            });

            // NEW: Call the function to load and render the UI here
            if (typeof window.loadConfigAndRenderUI === 'function') {
                window.loadConfigAndRenderUI();
            }

            resolve(keycloak.idToken);

        } else {
            console.log("Not authenticated.");
            window.appState.isAuthenticated = false;
            window.appState.idToken = null;
            window.appState.user = {};
            
            // Call a function from app.js to render the profile if it exists
            if (typeof window.renderUserProfile === 'function') {
                window.renderUserProfile();
            }
            
            reject(new Error("User not authenticated."));
        }
    }).catch(error => {
        console.error("Keycloak initialization error:", error);
        reject(error);
    });
});